import { SpineItem } from '../types/epub';
import { ScaleBehavior } from '../components/app/SheetScaler';
import { FitStyle } from '../utils/view';
import { AppStates } from '../reducers/app';
import { createSelector } from 'reselect';
import { Rectangle } from 'ark-common';
import { Dimension } from '../types/common';
import { isPopupPanelID } from '../reducers/pages';

export interface FrameSizeSelectorProps {
  readonly item: SpineItem;
  readonly panelID: string;
}

const getSpineItem = (_state: AppStates, props: FrameSizeSelectorProps): SpineItem => props.item;

const getContainerRect = (state: AppStates, props: FrameSizeSelectorProps): Rectangle => {
  const { view } = state;
  if (isPopupPanelID(props.panelID)) {
    const rect = view.popupRects[props.panelID];
    if (rect === undefined) {
      throw new Error('Unexpected state.');
    }
    return rect;
  }
  return view.tabPanelAreaRect;
};

const getContentSize = (state: AppStates, props: FrameSizeSelectorProps): Dimension | undefined =>
  state.view.frameContentSizes[props.panelID];

export const frameSizeSelector = createSelector(
  [getSpineItem, getContainerRect, getContentSize],
  (item: SpineItem, containerRect: Rectangle, contentSize: Dimension | undefined) => {
    return getFrameSize(item, containerRect.width, containerRect.height, contentSize);
  });

export interface FrameSizeSelectorResult {
  readonly width: number;
  readonly height: number;
  readonly scaleBehavior: ScaleBehavior;
}

const getFrameSize = (
  item: SpineItem,
  containerWidth: number,
  containerHeight: number,
  contentSize: Dimension | undefined,
): FrameSizeSelectorResult => {
  if (item.fxl) {
    if (contentSize !== undefined) {
      return { ...contentSize, scaleBehavior: item.scaleBehavior };
    }
    return item; // SpineItem has all properties of FrameSizeResult.
  }

  if (item.fitStyle === FitStyle.FitScreen) {
    // No autosize, no transform simple iframe mode.
    return {
      width: containerWidth,
      height: containerHeight,
      scaleBehavior: ScaleBehavior.Resize, // TODO : Change to "none".
    };
  }

  if (contentSize === undefined) {
    // The content size hasn't been notified yet.
    return {
      width: containerWidth,
      height: containerHeight,
      scaleBehavior: ScaleBehavior.Transform,
    };
  }

  if (item.fitStyle === FitStyle.FitWidth) {
    // horizontal-tb autosize reflowable page.
    return {
      width: containerWidth,
      height: contentSize.height,
      scaleBehavior: ScaleBehavior.Transform,
    };
  }

  // vertical-rl autosize reflowable page.
  return {
    width: contentSize.width,
    height: containerHeight,
    scaleBehavior: ScaleBehavior.Transform,
  };
};
